import { createElement, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { motionOn } from './motion'
import { easeOut, springEasing } from './springs'

/**
 * Numbers that move (APP-DESIGN-DIRECTION 3.8). Every helper keeps the final value in the DOM from the first frame as
 * visually hidden text; the moving digits are `aria-hidden`. So a screen reader, a failed animation and reduced motion all
 * get the truth, and nothing ever shows a value that is not real at rest.
 */

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/**
 * The band steps a reveal counts through: 0.5 apart from `max(1.0, band - 2.5)` up to the band. Never 0.0, never below
 * 1.0, never through a value that is not a half band. A band below 1.0 (impossible for a showable band) returns [band].
 */
export function bandSteps(band: number, span = 2.5): number[] {
  if (!Number.isFinite(band) || band < 1) return [band]
  const start = Math.max(1, Math.round((band - span) * 2) / 2)
  const steps: number[] = []
  for (let value = start; value < band - 0.001; value += 0.5) steps.push(Math.round(value * 2) / 2)
  steps.push(band)
  return steps
}

/**
 * Which step of `steps` shows at `progress` (0..1). Ease-out: the early half bands pass quickly and the steps slow into
 * the band, and the last step arrives exactly at `progress` 1, so the numeral, the end of the arc sweep and the pop that
 * follows land together (APP-DESIGN-DIRECTION 3.10.1).
 */
export function stepAt(steps: readonly number[], progress: number): number {
  if (steps.length === 0) return 0
  const n = steps.length
  if (progress >= 1 || n === 1) return steps[n - 1]!
  // A gentle ease-out (each interval longer than the one before) over n - 1 intervals; `floor` keeps the final step for
  // progress 1 exactly, never early. For 3.5 to 6.0 the steps arrive at about 14, 29, 46, 66 and 100% of the sweep.
  const eased = 1 - Math.pow(1 - Math.max(0, progress), 1.5)
  return steps[Math.min(n - 2, Math.floor(eased * (n - 1)))]!
}

export interface CountOptions {
  /** Start value. Integers count from here; default 0 (never used for bands: bands use `bandSteps`). */
  from?: number
  duration?: number
  delay?: number
  /** Start counting (the caller decides: in view, first view per session). False shows the final value. */
  play: boolean
  /** Count in these discrete steps instead of integers (a band reveal). */
  steps?: readonly number[]
}

/**
 * The number to draw at this frame. Without motion, without `play`, or after it finishes it is the final value. Integers
 * round on the way; `steps` jump between half bands.
 */
export function useCountUp(value: number, { from = 0, duration = 700, delay = 0, play, steps }: CountOptions): number {
  const [shown, setShown] = useState(value)
  const frame = useRef(0)
  useIsomorphicLayoutEffect(() => {
    if (!play || !motionOn() || typeof window.requestAnimationFrame !== 'function' || !Number.isFinite(value)) {
      setShown(value)
      return
    }
    const first = steps && steps.length ? steps[0]! : from
    setShown(first)
    let start = 0
    const tick = (now: number) => {
      if (!start) start = now
      const elapsed = now - start - delay
      if (elapsed < 0) { frame.current = window.requestAnimationFrame(tick); return }
      const progress = Math.min(1, elapsed / duration)
      setShown(steps && steps.length ? stepAt(steps, progress) : Math.round(from + (value - from) * easeOut(progress)))
      if (progress < 1) frame.current = window.requestAnimationFrame(tick)
    }
    frame.current = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame.current)
    // `steps` is compared by content: a new array with the same values must not restart the count.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, from, duration, delay, play, steps?.join(',')])
  return shown
}

export interface CountUpProps extends CountOptions {
  value: number
  /** How a number is written (a band with one decimal, a count with its unit). Default `String`. */
  format?: (value: number) => string
  className?: string
}

/** A counting number: the moving digits are hidden from assistive tech, the final text sits beside them in `.r5-sr`. */
export function CountUp({ value, format = String, className, ...options }: CountUpProps): ReactNode {
  const shown = useCountUp(value, options)
  const classes = ['r5-count', className].filter(Boolean).join(' ')
  // At rest (no motion, or the count has landed) the number is plain text: one node, nothing duplicated.
  if (shown === value) return createElement('span', { className: classes }, format(value))
  return createElement('span', { className: classes },
    createElement('span', { 'aria-hidden': 'true', className: 'r5-count__digits' }, format(shown)),
    createElement('span', { className: 'r5-sr' }, format(value)),
  )
}

export interface RollingNumberProps {
  /** The text of the number as it should read now ("24", "6.5"). */
  value: string
  className?: string
}

/**
 * A number that changes while on screen (tab counts, live counts, rank changes): the digits that changed slide 0.6em with
 * a 2px blur mid-move on the snappy spring; unchanged digits stay still. Tabular figures keep widths steady.
 */
export function RollingNumber({ value, className }: RollingNumberProps): ReactNode {
  const ref = useRef<HTMLSpanElement>(null)
  const previous = useRef(value)
  useIsomorphicLayoutEffect(() => {
    const before = previous.current
    previous.current = value
    const element = ref.current
    if (before === value || !element || !motionOn()) return
    const up = Number.parseFloat(value) >= Number.parseFloat(before) ? 1 : -1
    const cells = element.querySelectorAll<HTMLElement>('[data-digit]')
    cells.forEach((cell, index) => {
      // Compare from the end so a new leading digit (9 to 10) does not move the units.
      const fromEnd = cells.length - 1 - index
      const old = before[before.length - 1 - fromEnd]
      if (old === value[value.length - 1 - fromEnd] || typeof cell.animate !== 'function') return
      cell.animate([
        { transform: `translateY(${0.6 * up}em)`, opacity: 0, filter: 'blur(2px)' },
        { transform: `translateY(${0.15 * up}em)`, opacity: 0.8, filter: 'blur(1px)', offset: 0.5 },
        { transform: 'none', opacity: 1, filter: 'blur(0)' },
      ], { duration: 320, easing: springEasing('snappy') })
    })
  }, [value])
  return createElement('span', { ref, className: ['r5-roll num', className].filter(Boolean).join(' ') },
    [...value].map((char, index) => createElement('span', { key: `${index}-${value.length}`, 'data-digit': '', className: 'r5-roll__d' }, char)),
  )
}
